import { Metric } from '@enums';
import DataQuestionIcon from '@icons/data-question.svg?react';
import ErrorIcon from '@icons/error.svg?react';
import {
  formatMetricValue,
  isSupportedChartType,
  resolveBarColors
} from '@utils';

function formatIndexLabel(index?: number | string) {
  if (index === undefined) {
    return '';
  }

  if (typeof index === 'string') {
    return index;
  }

  return formatMetricValue(index, Metric.Index);
}

import type { ChartData, ChartType } from 'types/common';
import Styled from './Chart.styled';
import HorizontalBarChart from './HorizontalBarChart';
import HorizontalStackedBarChart from './HorizontalStackedBarChart';
import PieChart from './PieChart';
import SourceKeys from './SourceKeys';

interface Props {
  type: ChartType;
  data: ChartData[];
  metric: Metric;
  colors?: readonly string[];
  hasMultipleAudiences?: boolean;
}

function Chart(props: Props) {
  const { data, metric, type, colors, hasMultipleAudiences } = props;
  const resolvedColors =
    colors && colors.length > 0
      ? colors
      : resolveBarColors(data, type === 'bar');

  const getChart = () => {
    switch (type) {
      case 'pie':
        return <PieChart colors={resolvedColors} data={data} />;
      case 'stacked-bar':
        return (
          <HorizontalStackedBarChart
            colors={resolvedColors}
            data={data}
            metric={metric}
            renderLabel={(segment) => formatIndexLabel(segment.index)}
          />
        );
      case 'bar':
        return (
          <HorizontalBarChart
            colors={resolvedColors}
            data={data}
            hasMultipleAudiences={hasMultipleAudiences}
            metric={metric}
          />
        );
      default:
        return null;
    }
  };

  const getContent = () => {
    if (!data || data.length === 0) {
      return (
        <Styled.FallbackState>
          <DataQuestionIcon />
          <Styled.FallbackText>No data</Styled.FallbackText>
        </Styled.FallbackState>
      );
    }

    if (isSupportedChartType(type)) {
      return (
        <>
          {getChart()}
          {type !== 'bar' ? (
            <SourceKeys
              colors={resolvedColors}
              labels={data.map((item) => item.name)}
              title="Key:"
            />
          ) : null}
        </>
      );
    }

    return (
      <Styled.FallbackState>
        <ErrorIcon />
        <Styled.FallbackText>Not supported chart type</Styled.FallbackText>
      </Styled.FallbackState>
    );
  };

  return <Styled.Wrapper>{getContent()}</Styled.Wrapper>;
}

export default Chart;
